import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useSignup } from '../hooks/auth-actions.js';
import { describeError, isErrorCode, mergeFormClasses } from './shared.js';
const schema = z.object({
    email: z.string().trim().min(1, 'Enter your email.').email('Enter a valid email.'),
    password: z.string().min(8, 'Use at least 8 characters.'),
    displayName: z.string().trim().max(120).optional().or(z.literal('')),
});
/**
 * Self-service signup form. After submit, branches on the server's
 * `requiresEmailVerification` flag: when true the form transitions to a
 * "check your email" panel; when false (and a session was returned) it calls
 * `onSuccess` so the host can route to the app.
 */
export function SignupForm(props) {
    const { onSuccess, onVerificationRequired } = props;
    const classes = mergeFormClasses(props.classNames);
    const signup = useSignup();
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [displayName, setDisplayName] = React.useState('');
    const [pendingVerificationEmail, setPendingVerificationEmail] = React.useState(null);
    const [validation, setValidation] = React.useState({});
    const serverError = describeError(signup.error, props.errorMessages);
    const emailTaken = isErrorCode(signup.error, 'IDENTITY_ALREADY_EXISTS');
    const weakPassword = isErrorCode(signup.error, 'WEAK_PASSWORD');
    if (pendingVerificationEmail !== null) {
        return (_jsx("div", { className: classes.form, children: _jsxs("p", { className: classes.success, role: "status", children: ["Account created. We sent a verification link to", ' ', _jsx("strong", { children: pendingVerificationEmail }), ". Open it to finish setting up your account."] }) }));
    }
    const handleSubmit = async (event) => {
        event.preventDefault();
        const parsed = schema.safeParse({ email, password, displayName });
        if (!parsed.success) {
            const fieldErrors = parsed.error.flatten().fieldErrors;
            setValidation({
                email: fieldErrors.email?.[0],
                password: fieldErrors.password?.[0],
                displayName: fieldErrors.displayName?.[0],
            });
            return;
        }
        setValidation({});
        try {
            const result = await signup.run({
                email: parsed.data.email,
                password: parsed.data.password,
                ...(parsed.data.displayName !== undefined && parsed.data.displayName !== ''
                    ? { displayName: parsed.data.displayName }
                    : {}),
            });
            if (result.requiresEmailVerification) {
                setPendingVerificationEmail(parsed.data.email);
                onVerificationRequired?.(parsed.data.email);
            }
            else if (result.result !== undefined) {
                onSuccess?.(result.result);
            }
        }
        catch {
            // Error captured by the hook.
        }
    };
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-signup-email", children: "Email" }), _jsx("input", { id: "auth-ui-signup-email", className: classes.input, type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => setEmail(event.target.value), "aria-invalid": validation.email !== undefined || emailTaken ? 'true' : undefined }), validation.email !== undefined ? (_jsx("p", { className: classes.error, role: "alert", children: validation.email })) : null] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-signup-password", children: "Password" }), _jsx("input", { id: "auth-ui-signup-password", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: password, onChange: (event) => setPassword(event.target.value), "aria-invalid": validation.password !== undefined || weakPassword ? 'true' : undefined }), validation.password !== undefined ? (_jsx("p", { className: classes.error, role: "alert", children: validation.password })) : null] }), _jsxs("div", { className: classes.field, children: [_jsxs("label", { className: classes.label, htmlFor: "auth-ui-signup-name", children: ["Display name ", _jsx("span", { className: classes.hint, children: "(optional)" })] }), _jsx("input", { id: "auth-ui-signup-name", className: classes.input, type: "text", autoComplete: "name", value: displayName, onChange: (event) => setDisplayName(event.target.value) })] }), serverError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: serverError })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: signup.loading, children: signup.loading ? 'Creating account…' : 'Create account' })] }));
}
//# sourceMappingURL=SignupForm.js.map